import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Ban, Video, ShieldCheck } from "lucide-react";
import { PageHead, Wrap, H2, P, Prose, Impact, DontList, CheckList, Callout, Disclaimer } from "@/components/Content";
import { ChainOfCustody } from "@/components/ChainOfCustody";

export default function ShippingInsurance() {
  return (
    <div className="bg-ink min-h-screen pb-24">
      <PageHead eyebrow="Shipping & Insurance" title={<>DOCUMENTED BEFORE<br />IT LEAVES OUR HANDS.</>} tone="cyan"
        sub="CardScience HQ maintains a strict intake and chain-of-custody process. Read this before shipping anything." />

      <Wrap className="py-16 space-y-14">
        {/* Pre-approval */}
        <div>
          <H2>DO NOT MAIL US A COLLECTIBLE UNTIL YOUR SUBMISSION IS APPROVED.</H2>
          <Prose className="mt-5">
            <P>Every card, comic or collectible must first go through the CardScience HQ submission process.</P>
            <P>Customers must receive approval and shipping instructions before sending a collectible.</P>
            <P>An unsolicited shipment does not automatically create a service agreement.</P>
            <P>A shipment sent outside our approved process may be refused, rejected or returned.</P>
          </Prose>
        </div>

        {/* Shipping protocol */}
        <Callout>
          <p className="eyebrow mb-3 flex items-center gap-2"><Ban size={13} className="text-[color:var(--magenta)]" /> Mandatory Shipping Protocol</p>
          <H2>IF YOU DO NOT FOLLOW THE SHIPPING PROTOCOL,<br />DO NOT SHIP THE COLLECTIBLE.</H2>
          <Prose className="mt-5">
            <P>CardScience HQ maintains a strict intake and chain-of-custody process.</P>
            <P>Cards, comics and other collectibles must be submitted according to the instructions associated with the approved submission.</P>
            <P>Any card, comic or collectible mailed, shipped or delivered without following the required CardScience HQ submission and shipping protocol may be:</P>
          </Prose>
          <div className="mt-4">
            <DontList items={[
              "Refused upon delivery",
              "Returned to the sender",
              "Returned at the sender’s expense where permitted",
              "Declared ineligible for service",
              "Associated with a cancelled submission",
            ]} />
          </div>
          <Prose className="mt-5">
            <P>Sending an unsolicited collectible does not obligate CardScience HQ to perform work.</P>
            <P>Unauthorized shipments do not automatically create acceptance of service, acceptance of value, acceptance of declared condition or acceptance of liability beyond obligations imposed by applicable law.</P>
          </Prose>
          <p className="mt-6 h-display text-xl text-holo">NO APPROVED SUBMISSION.<br />NO SHIPMENT.<br />NO EXCEPTIONS.</p>
        </Callout>

        {/* $50+ documentation */}
        <div>
          <p className="eyebrow mb-3 flex items-center gap-2"><Video size={13} className="text-[color:var(--cyan)]" /> $50+ Shipping Documentation</p>
          <H2>DOCUMENTED BEFORE IT LEAVES OUR HANDS.</H2>
          <P className="mt-4">For collectibles with a declared value of $50 or more, CardScience HQ uses a documented return-packing process. Our documentation may include:</P>
          <div className="mt-4">
            <CheckList items={[
              "Video of the actual card or collectible",
              "Front of the collectible where applicable",
              "Back of the collectible where applicable",
              "Condition immediately before packaging",
              "Protective sleeve, holder, comic bag, board, envelope or protective packaging as applicable",
              "The shipping box or mailer",
              "Placement of the collectible inside the shipping package",
              "Video of the package being closed",
              "Video of the package being sealed with the collectible inside",
              "Identification of the submission associated with the package",
              "Shipping and tracking information",
            ]} />
          </div>
          <P className="mt-5">For qualifying shipments, our preferred documentation process is a continuous recording showing the collectible, packaging process and final sealing whenever practical.</P>
          <div className="mt-6 rounded-2xl holo-border p-6 text-center">
            <p className="h-display text-lg sm:text-xl text-holo leading-tight">WE DOCUMENT YOUR COLLECTIBLE BEFORE WE TOUCH IT —<br />AND WE DOCUMENT IT AGAIN BEFORE IT LEAVES OUR HANDS.</p>
          </div>
        </div>

        {/* $500+ insurance */}
        <Callout holo>
          <p className="eyebrow mb-3 flex items-center gap-2"><ShieldCheck size={13} className="text-[color:var(--cyan)]" /> $500+ Mandatory Insurance</p>
          <H2>$500+ COLLECTIBLES REQUIRE INSURANCE.</H2>
          <Prose className="mt-5">
            <P>Any card, comic or other approved collectible with a declared value of $500 or more must use the insurance requirements specified by CardScience HQ for applicable shipping.</P>
            <P className="text-white font-semibold">Insurance is not optional for qualifying $500+ submissions.</P>
            <P>The customer is responsible for accurately declaring the value of the collectible.</P>
            <P>CardScience HQ may request reasonable documentation supporting a declared value.</P>
            <P>Insurance is subject to the carrier or insurance provider’s own terms, exclusions, limits, claim procedures and eligibility requirements.</P>
            <P>CardScience HQ cannot guarantee that a carrier or third-party insurer will approve or pay a claim.</P>
            <P>Customers may elect additional shipping protection on lower-value collectibles where available.</P>
          </Prose>
        </Callout>

        {/* Chain of custody */}
        <div>
          <p className="eyebrow mb-5">Chain Of Custody</p>
          <ChainOfCustody />
        </div>

        <div className="flex flex-wrap gap-3">
          <Link to="/submit" className="btn-holo inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm">SUBMIT A COLLECTIBLE <ArrowRight size={16} /></Link>
          <Link to="/how-it-works" className="btn-ghost inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm">HOW IT WORKS</Link>
        </div>
      </Wrap>
    </div>
  );
}
